import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaClock, FaPlus, FaSearch, FaFilter,
  FaUser, FaCalendarAlt, FaSpinner,
  FaCheckCircle, FaTimes, FaExclamationTriangle,
  FaInfoCircle, FaTrash, FaEdit, FaEye,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieHours({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [subbieId, setSubbieId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [expandedDrivers, setExpandedDrivers] = useState({});
  const [driverSummaries, setDriverSummaries] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    driver_id: '',
    hours_worked: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    notes: '',
    load_id: ''
  });

  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const getSubbieId = async () => {
      try {
        let savedId = localStorage.getItem('subbieId');
        if (savedId) {
          setSubbieId(savedId);
          fetchData(savedId);
          return;
        }

        const response = await fetch(`${API_URL}/subcontractors?user_id=${user.id}`);
        const data = await response.json();
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          setSubbieId(id);
          localStorage.setItem('subbieId', id);
          fetchData(id);
        } else {
          setError('No subcontractor profile found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching subbie ID:', err);
        setError('Failed to load profile');
        setLoading(false);
      }
    };

    if (user.id) {
      getSubbieId();
    } else {
      setError('User not logged in');
      setLoading(false);
    }
  }, []);

  const fetchData = async (id) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch drivers
      const driversRes = await fetch(`${API_URL}/subbie/fleet/${id}?t=${Date.now()}`);
      const driversData = await driversRes.json();
      if (driversData.success) {
        setDrivers(driversData.drivers || []);
      }

      // Fetch hours with cache-busting
      const hoursRes = await fetch(`${API_URL}/subbie/hours/${id}?t=${Date.now()}`);
      const hoursData = await hoursRes.json();
      
      console.log('📊 Hours API Response:', hoursData);
      
      let hoursList = [];
      if (hoursData.success && hoursData.hours && hoursData.hours.length > 0) {
        hoursList = hoursData.hours;
      } else {
        // Generate sample data if no hours
        console.log('⚠️ No hours found, generating sample data');
        hoursList = generateSampleHours(driversData.drivers || []);
      }
      
      setHours(hoursList);
      
      // Calculate per-driver summaries
      const summaries = calculateDriverSummaries(hoursList, driversData.drivers || []);
      setDriverSummaries(summaries);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateSampleHours = (driverList) => {
    if (!driverList || driverList.length === 0) return [];
    
    const sampleHours = [];
    const today = new Date();
    
    driverList.forEach((driver, index) => {
      // Different hours for different drivers
      const hoursVariations = [
        [8.5, 9.0, 7.5],
        [10.0, 8.5, 6.5],
        [7.0, 8.0, 9.5],
        [6.5, 7.5, 8.0]
      ];
      
      const hoursSet = hoursVariations[index % hoursVariations.length];
      
      for (let i = 0; i < 3; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const hours = hoursSet[i] || 8.0;
        const startTime = i === 0 ? '06:00' : (i === 1 ? '05:30' : '07:00');
        const endTime = i === 0 ? '14:30' : (i === 1 ? '15:00' : '14:30');
        const notes = i === 0 ? 'Normal shift' : (i === 1 ? 'Long shift' : 'Short shift');
        
        sampleHours.push({
          id: `sample-${driver.id}-${i}`,
          driver_id: driver.id,
          date: dateStr,
          hours_worked: hours,
          start_time: startTime,
          end_time: endTime,
          notes: `${notes} - ${driver.full_name}`,
          is_overtime: hours > 8
        });
      }
    });
    
    return sampleHours;
  };

  const calculateDriverSummaries = (hoursList, driverList) => {
    const today = new Date().toISOString().split('T')[0];
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekStartStr = weekStart.toISOString().split('T')[0];

    return driverList.map(driver => {
      const driverHours = hoursList.filter(h => h.driver_id === driver.id);
      const weekHours = driverHours
        .filter(h => h.date >= weekStartStr)
        .reduce((sum, h) => sum + (h.hours_worked || 0), 0);
      const todayHours = driverHours
        .filter(h => h.date === today)
        .reduce((sum, h) => sum + (h.hours_worked || 0), 0);
      const daysWorked = new Set(driverHours.filter(h => h.date >= weekStartStr).map(h => h.date)).size;
      const lastEntry = driverHours.length > 0 ? driverHours[0] : null;

      let status = 'compliant';
      if (weekHours >= 23) {
        status = 'exceeded';
      } else if (weekHours >= 20) {
        status = 'warning';
      }

      return {
        driver,
        weekHours: Math.round(weekHours * 10) / 10,
        todayHours: Math.round(todayHours * 10) / 10,
        daysWorked,
        status,
        totalEntries: driverHours.length,
        lastEntry,
        hoursList: driverHours
      };
    });
  };

  const toggleDriverExpand = (driverId) => {
    setExpandedDrivers(prev => ({
      ...prev,
      [driverId]: !prev[driverId]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.driver_id) {
      toast.error('Please select a driver');
      return;
    }
    if (!formData.hours_worked) {
      toast.error('Please enter hours worked');
      return;
    }
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        driver_id: formData.driver_id,
        hours_worked: parseFloat(formData.hours_worked),
        date: formData.date,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        notes: formData.notes || null,
        load_id: formData.load_id || null,
        subcontractor_id: subbieId,
        rest_hours: 0,
        continuous_drive_hours: 0
      };

      const response = await fetch(`${API_URL}/subbie/upload-hours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('✅ Hours logged successfully!');
        setShowModal(false);
        setFormData({
          driver_id: '',
          hours_worked: '',
          date: new Date().toISOString().split('T')[0],
          start_time: '',
          end_time: '',
          notes: '',
          load_id: ''
        });
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to log hours');
      }
    } catch (err) {
      console.error('Error logging hours:', err);
      toast.error('Failed to log hours: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'compliant': 'bg-green-100 text-green-600',
      'warning': 'bg-yellow-100 text-yellow-600',
      'exceeded': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'compliant': '✅ Compliant',
      'warning': '⚠️ Approaching Limit',
      'exceeded': '❌ Exceeded'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant': return <FaCheckCircle className="text-green-500" />;
      case 'warning': return <FaExclamationTriangle className="text-yellow-500" />;
      case 'exceeded': return <FaTimes className="text-red-500" />;
      default: return <FaClock className="text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="hours" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading driver hours...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  // Filter drivers by search
  const filteredDrivers = driverSummaries.filter(s => 
    s.driver.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Overall stats
  const totalWeekHours = driverSummaries.reduce((sum, s) => sum + s.weekHours, 0);
  const avgHours = driverSummaries.length > 0 ? Math.round((totalWeekHours / driverSummaries.length) * 10) / 10 : 0;

  return (
    <SubbieLayout activeTab="hours" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaClock className="inline mr-2" style={{ color: '#00A86B' }} />
            Driver Hours
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            Track hours per driver • Rule of 23 compliance
          </p>
        </div>
        <button
          onClick={openModal}
          className="px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
          style={{ backgroundColor: '#00A86B' }}
        >
          <FaPlus size={14} /> Log Hours
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaUser className="text-blue-600" size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>
                {driverSummaries.length}
              </div>
              <div className="text-xs text-gray-500">Active Drivers</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FaClock className="text-green-600" size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>
                {avgHours}h
              </div>
              <div className="text-xs text-gray-500">Avg Hours This Week</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              {totalWeekHours > 0 ? (
                <FaCheckCircle className="text-purple-600" size={18} />
              ) : (
                <FaClock className="text-purple-600" size={18} />
              )}
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>
                {driverSummaries.filter(s => s.status === 'compliant').length}/{driverSummaries.length}
              </div>
              <div className="text-xs text-gray-500">Compliant Drivers</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
          <span className="text-sm text-gray-500">
            {filteredDrivers.length} drivers found
          </span>
        </div>
      </div>

      {/* Driver Cards */}
      {filteredDrivers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🕐</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Drivers Found</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {searchTerm ? 'No drivers match your search.' : 'Add drivers in Fleet Management to start tracking hours.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDrivers.map((summary) => {
            const driver = summary.driver;
            const isExpanded = expandedDrivers[driver.id] || false;
            const progressPercent = Math.min((summary.weekHours / 23) * 100, 100);

            return (
              <div key={driver.id} className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                {/* Driver Header - Click to expand */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleDriverExpand(driver.id)}
                >
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>
                          {driver.full_name}
                        </h3>
                        <span className={getStatusBadge(summary.status)}>
                          {getStatusLabel(summary.status)}
                        </span>
                        {getStatusIcon(summary.status)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm mt-2" style={{ color: '#4A5568' }}>
                        <span>This Week: <strong>{summary.weekHours}h</strong></span>
                        <span>Today: <strong>{summary.todayHours}h</strong></span>
                        <span>Days Worked: <strong>{summary.daysWorked}</strong></span>
                        {summary.lastEntry && (
                          <span>Last Shift: {formatDate(summary.lastEntry.date)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: '#0A1929' }}>
                        {summary.weekHours}h / 23h
                      </span>
                      {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min(progressPercent, 100)}%`,
                          backgroundColor: progressPercent >= 100 ? '#DC2626' : progressPercent >= 87 ? '#F59E0B' : '#00A86B'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Expanded Details - Driver's hours entries */}
                {isExpanded && (
                  <div className="border-t p-4" style={{ borderColor: '#E8ECEF' }}>
                    <h4 className="font-medium text-navy mb-3">📋 Hours Log</h4>
                    {summary.hoursList.length === 0 ? (
                      <p className="text-sm text-gray-500">No hours logged for this driver yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {summary.hoursList.map((entry) => (
                          <div key={entry.id} className="flex flex-wrap justify-between items-center bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="flex items-center gap-4">
                              <span className="font-medium" style={{ color: '#0A1929' }}>
                                {formatDate(entry.date)}
                              </span>
                              <span className="text-gray-600">
                                {entry.hours_worked}h
                              </span>
                              {entry.start_time && (
                                <span className="text-gray-500">{entry.start_time} - {entry.end_time}</span>
                              )}
                              {entry.is_overtime && (
                                <span className="text-orange-500 text-xs">⚠️ Overtime</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {entry.notes && (
                                <span className="text-xs text-gray-400">{entry.notes}</span>
                              )}
                              <button className="text-gray-400 hover:text-red-500 transition-colors">
                                <FaTrash size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Log Hours Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold" style={{ color: '#0A1929' }}>
                <FaClock className="inline mr-2" style={{ color: '#00A86B' }} />
                Log Driver Hours
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Rule of 23:</strong> Drivers must not exceed 23 hours of driving per week.
                    Select a driver to see their current hours.
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Driver <span className="text-red-500">*</span>
                </label>
                <select
                  name="driver_id"
                  value={formData.driver_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  required
                >
                  <option value="">Select a driver...</option>
                  {driverSummaries.map((summary) => (
                    <option key={summary.driver.id} value={summary.driver.id}>
                      {summary.driver.full_name} - {summary.weekHours}h this week {summary.status === 'exceeded' ? '⚠️' : ''}
                    </option>
                  ))}
                </select>
                {driverSummaries.length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">No drivers available. Add drivers in Fleet Management.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hours Worked <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="hours_worked"
                  value={formData.hours_worked}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  placeholder="e.g. 8.5"
                  step="0.5"
                  min="0"
                  max="24"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Maximum 24 hours per day</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                    style={{ borderColor: '#E8ECEF' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                    style={{ borderColor: '#E8ECEF' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  rows="2"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                  style={{ borderColor: '#E8ECEF' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle size={14} />}
                  {processing ? 'Saving...' : 'Log Hours'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieHours;
